import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '@/shared/lib/cn';

/** Anything else is given to the text itself, the element that takes focus. */
interface TooltipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'content'> {
  /** What the hint says. It may hold a link: the hint stays open while it is used. */
  content: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Whether the text takes focus by itself. False inside a link or a button, which is
   * what takes the focus there: the hint then follows the pointer and the focus of that element.
   */
  focusable?: boolean;
}

/** How close to the edge of the window a hint may come, in pixels. */
const MARGIN = 8;
/** How long a hint outlives the pointer: time to travel from the text to the hint. */
const LINGER_MS = 150;

/**
 * A hint shown next to a piece of text: on hover, on keyboard focus, and on touch
 * (a tap focuses it). Escape, moving away or tapping elsewhere closes it; the pointer
 * may travel onto the hint itself, which then stays.
 *
 * The text is reachable with Tab and tied to its hint with `aria-describedby`, so a
 * screen reader reads the hint after the text.
 */
export function Tooltip({ content, children, className, focusable = true, ...rest }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  // How far the hint is moved sideways to stay inside the window.
  const [shift, setShift] = useState(0);
  const hint = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open || !hint.current) return;
    const box = hint.current.getBoundingClientRect();
    const overRight = box.right - (document.documentElement.clientWidth - MARGIN);
    if (overRight > 0) setShift(-overRight);
    else if (box.left < MARGIN) setShift(MARGIN - box.left);
  }, [open]);

  const leaving = useRef<ReturnType<typeof setTimeout>>(undefined);
  const stay = () => clearTimeout(leaving.current);
  useEffect(() => stay, []);

  const show = () => {
    stay();
    setOpen(true);
  };
  const close = () => {
    stay();
    setOpen(false);
    setShift(0);
  };

  return (
    <span
      className="relative"
      onMouseEnter={show}
      onMouseLeave={() => {
        stay();
        leaving.current = setTimeout(close, LINGER_MS);
      }}
      onFocus={show}
      onBlur={(event) => {
        // Focus moving into the hint (to its link) keeps it open.
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          close();
        }
      }}
    >
      <span {...rest} tabIndex={focusable ? 0 : undefined} aria-describedby={open ? id : undefined} className={className}>
        {children}
      </span>
      {open ? (
        // The gap to the text is padding, not margin: the pointer never leaves on its way.
        <span
          ref={hint}
          style={{ transform: `translateX(${shift}px)` }}
          className="absolute top-full left-0 z-20 block w-72 max-w-[calc(100vw-1rem)] pt-1"
        >
          <span
            id={id}
            role="tooltip"
            className={cn(
              'block rounded-xl border border-line bg-surface p-3 shadow-lg',
              'text-left text-sm font-normal whitespace-normal text-ink',
            )}
          >
            {content}
          </span>
        </span>
      ) : null}
    </span>
  );
}
